import type {
  IValue,
  IValueCalculator,
  VertexDependencies,
  VertexDescriptor,
  VertexDescriptorCollection
} from './Graph';
import { Graph, TypedValue } from './Graph';
import type { Type } from './Type';
import type { BuildInfoMap } from './DataFlowBuilder';
import { FunctorValueCalculator } from './DataFlowBuilder';
import { InputValueCalculator, BuildInfo } from './DataFlowBuilder';
import { DataFlowBuilder, UnknownNameError } from './DataFlowBuilder';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import { CallbackManager } from '@/engine/Utilities/CallbackManager';
import { requireEqualPrototypes } from './Utils';
import { FunctorsRegistry } from './Functor';
import { TypesRegistry } from './Type';

export class NotTerminalVertex extends Error {
  public constructor(name: string) {
    super('Terminal vertex expected but it is dependent instead: [' + name + '].');
    Object.setPrototypeOf(this, NotTerminalVertex.prototype);
  }
}

export type AlgorithmExtension = (builder: DataFlowBuilder) => void;

export interface ValueConstructor<T extends IValue = IValue> {
  new (...args: Array<any>): T;
  readonly Type: Type;
}
export type NamedValue = [string, IValue | undefined];
export type ValuesCollection = Array<NamedValue>;

type _NamedVertexMap = Map<VertexDescriptor, BuildInfo>;

export type OnChangedForCallbackT<T extends IValue> = (value: T | undefined) => void;
export type OnChangedCallbackT<T extends IValue> = (name: string, value: T | undefined) => void;
export type OnFailedCallback = (namesList: Array<string>) => void;

export interface VertexInfo {
  id: VertexDescriptor;
  valueType: string;
  name?: string;
  value?: unknown;
  calculator?: {
    name: string;
    dependencies: VertexDescriptorCollection;
  };
}

export interface ExtensionInfo {
  id: string;
  vertices: VertexDescriptorCollection;
}

export interface AncestryInfo {
  value: IValue | undefined;
  type: string;
  name?: string;
  algorithm?: string;
  dependencies?: Array<number>;
}

export interface ITypedValueJsonIO {
  export: (type: Type, value: IValue | undefined) => unknown;
  import: (json: unknown, type: string, vertexName?: string) => IValue | undefined;
}

export interface IDataFlowGetter {
  getIValue: (name: string) => IValue | undefined;
  getValue: <T extends IValue>(name: string, constructor: ValueConstructor<T>) => T | undefined;
  getType: (name: string) => Type;
  has: (name: string) => boolean;
  getInputs: () => Array<string>;
  getOutputs: () => Array<string>;
  getAll: () => Array<string>;
  hasExtension: (extensionDescriptor: string) => boolean;
  getInputsFor: (names: Array<string>) => Array<string>;
  getOutputsOf: (names: Array<string>) => Array<string>;
  getAncestorsOf: (name: string, calculatorNames: Set<string>) => Map<number, AncestryInfo>;
}

export interface IDataFlowSetter {
  extendAsync: (extension: AlgorithmExtension, extensionDescriptor?: string, shouldNotify?: boolean) => Promise<void>;
  dropExtensionAsync: (extensionDescriptor: string) => Promise<void>;
  setValuesAsync: (values: ValuesCollection, shouldNotify?: boolean) => Promise<void>;
}

export interface IDataFlowCallbacks {
  addOnChangedCallbackForT: <T extends IValue>(name: string, func: OnChangedForCallbackT<T>) => CallbackSubscription;
  addOnChangedCallbackT: <T extends IValue>(func: OnChangedCallbackT<T>) => CallbackSubscription;
  addOnChangedCallback: (func: OnChangedCallbackT<IValue>) => CallbackSubscription;
  addOnFailedCallback: (func: OnFailedCallback) => CallbackSubscription;
}

export interface IDataFlowSerialization {
  exportVertexInfo: (io: ITypedValueJsonIO) => Array<VertexInfo>;
  importVertexInfo: (io: ITypedValueJsonIO, info: Array<VertexInfo>) => Promise<void>;
  exportExtensionInfo: () => Array<ExtensionInfo>;
  importExtensionInfo: (info: Array<ExtensionInfo>) => void;
}

export class DataFlow implements IDataFlowGetter, IDataFlowSetter, IDataFlowCallbacks, IDataFlowSerialization {
  private _graph: Graph = new Graph(); // not readonly only because of unit testing

  private readonly _namesMap: BuildInfoMap = new Map();

  private readonly _namedVertexMap: _NamedVertexMap = new Map();

  private readonly _algorithmExtensions: Map<string, VertexDescriptorCollection> = new Map();

  private readonly _onChangedCallbackManagerMap = new Map<string, CallbackManager<OnChangedForCallbackT<IValue>>>();

  private readonly _onChangedCallbackManager = new CallbackManager<OnChangedCallbackT<IValue>>();

  private readonly _onFailedCallbackManager = new CallbackManager<OnFailedCallback>();

  public async extendAsync(
    extension: AlgorithmExtension,
    extensionDescriptor?: string,
    shouldNotify = false
  ): Promise<void> {
    if (extensionDescriptor && this._algorithmExtensions.has(extensionDescriptor)) {
      throw new Error('Algorithm extension with same descriptor is already registered: ' + extensionDescriptor);
    }
    const builder = new DataFlowBuilder(this._graph, this._namesMap);
    extension(builder);
    // update _namedVertexMap
    this._namedVertexMap.clear();
    this._namesMap.forEach((value: BuildInfo): void => {
      this._namedVertexMap.set(value.vertex, value);
    });
    const createdVertices = await builder.buildAsync();
    if (extensionDescriptor) {
      this._algorithmExtensions.set(extensionDescriptor, createdVertices);
    }
    if (shouldNotify) {
      this._notifyVertices(createdVertices);
    }
  }

  public hasExtension(extensionDescriptor: string): boolean {
    return !!this._algorithmExtensions.get(extensionDescriptor);
  }

  public dropExtensionAsync(extensionDescriptor: string): Promise<void> {
    const orderedVertices = this._algorithmExtensions.get(extensionDescriptor);
    if (!orderedVertices) {
      throw new Error('Algorithm extension is not registered');
    }
    this._graph.deleteDependentVertices(orderedVertices);
    // update _namedVertexMap
    for (const vertex of orderedVertices) {
      const buildInfo = this._namedVertexMap.get(vertex);
      if (buildInfo) {
        this._namedVertexMap.delete(vertex);
        if (buildInfo.vertexName) {
          this._namesMap.delete(buildInfo.vertexName);
        }
      }
    }

    this._algorithmExtensions.delete(extensionDescriptor);

    return Promise.resolve();
  }

  private _updateInputVertices(values: ValuesCollection): VertexDescriptorCollection {
    const vertices: VertexDescriptorCollection = new Array<VertexDescriptor>(values.length);
    for (let i = 0; i < values.length; ++i) {
      const [name, value] = values[i];
      const info = this._namesMap.get(name);
      if (info === undefined) {
        throw new UnknownNameError(name);
      }
      if (!info.isInputVertex) {
        throw new NotTerminalVertex(name);
      }
      if (value) {
        const type = value.type;
        requireEqualPrototypes([info.type], [type]);
      }
      const vertex = info.vertex;
      const calculator = this._graph.getValueCalculator(vertex) as InputValueCalculator;
      calculator.setValue(new TypedValue(info.type, value));
      vertices[i] = vertex;
    }
    return vertices;
  }

  private _eraseVertices(vertices: VertexDescriptorCollection): void {
    for (const v of vertices) {
      this._graph.eraseVertex(v);
    }
  }

  private _notifyVertices(vertices: VertexDescriptorCollection): void {
    const failuresList: Array<string> = [];
    for (const v of vertices) {
      const info = this._namedVertexMap.get(v);
      if (info) {
        // we're notifying only named vertices clients
        if (!info.vertexName) {
          throw new Error('Vertex expected to have name');
        }
        const name = info.vertexName;
        const value = this._graph.getValue(info.vertex)?.value;
        this._onChangedCallbackManager.invoke(name, value);
        this._onChangedCallbackManagerMap.get(name)?.invoke(value);
        if (value === undefined) {
          failuresList.push(name);
        }
      }
    }
    if (failuresList.length !== 0) {
      this._onFailedCallbackManager.invoke(failuresList);
    }
  }

  private _getVertices(names: Array<string>): Array<VertexDescriptor> {
    return names.map((name: string): VertexDescriptor => {
      const info = this._namesMap.get(name);
      if (info === undefined) {
        throw new UnknownNameError(name);
      }
      return info.vertex;
    });
  }

  public getOutputsOf(names: Array<string>): Array<string> {
    const retNames: Array<string> = [];
    for (const v of this._graph.simpleTopologicalSort(this._getVertices(names))) {
      const name = this._namedVertexMap.get(v)?.vertexName;
      if (name) {
        retNames.push(name);
      }
    }
    return retNames;
  }

  public getInputsFor(names: Array<string>): Array<string> {
    const retNames: Array<string> = [];
    for (const d of this._graph.topologicalSortInv(this._getVertices(names))) {
      if (!d.dependencies) {
        // no dependencies = input vertex
        const name = this._namedVertexMap.get(d.vertex)?.vertexName;
        if (name) {
          retNames.push(name);
        }
      }
    }
    return retNames;
  }

  public getAncestorsOf(name: string, calculatorNames: Set<string>): Map<number, AncestryInfo> {
    const vertex = this._namesMap.get(name)?.vertex;
    if (vertex === undefined) {
      throw new UnknownNameError(name);
    }
    const ret = new Map<number, AncestryInfo>();
    ret.set(vertex, { value: this._graph.getValue(vertex)?.value, type: this._graph.getType(vertex).name, name: name });
    for (const d of this._graph.ancestryInfo([vertex])) {
      const info = ret.get(d.vertex);
      if (info) {
        const calculatorName = this._graph.getValueCalculator(d.vertex).name;
        if (calculatorName && calculatorNames.has(calculatorName)) {
          info.algorithm = calculatorName;
          info.dependencies = d.dependencies;
          if (info.dependencies) {
            for (const v of info.dependencies) {
              ret.set(v, {
                value: this._graph.getValue(v)?.value,
                type: this._graph.getType(v).name,
                name: this._namedVertexMap.get(v)?.vertexName
              });
            }
          }
        }
      }
    }
    return ret;
  }

  // Algorithm computation API
  //   sets values for vertices by name
  //   usage:
  //     algo.setValues([["val1", 5], ["val2", 2]]);
  public async setValuesAsync(values: ValuesCollection, shouldNotify = true): Promise<void> {
    const inpVertices = this._updateInputVertices(values);
    const dependencies = this._graph.topologicalSort(inpVertices);
    const outpVertices = dependencies.map((d) => d.vertex);
    this._eraseVertices(outpVertices);
    await this._graph.calculateVerticesAsync(dependencies);
    if (shouldNotify) {
      this._notifyVertices(outpVertices);
    }
  }

  // Algorithm computation API
  //   get values for vertex by name
  //   usage:
  //     algo.getIValue("val1");
  public getIValue(name: string): IValue | undefined {
    const info = this._namesMap.get(name);
    if (info === undefined) {
      throw new UnknownNameError(name);
    }
    return this._graph.getValue(info.vertex)?.value;
  }

  // Algorithm computation API
  //   get values for vertex by name
  //   usage:
  //     algo.getValue("val1");
  public getValue<T extends IValue>(name: string, constructor: ValueConstructor<T>): T | undefined {
    const value = this.getIValue(name);
    if (value === undefined) {
      return undefined;
    }
    requireEqualPrototypes([value.type], [constructor.Type]);
    return value as unknown as T;
  }

  // Algorithm computation API
  //   get values type for vertex by name
  //   usage:
  //     algo.getType("val1");
  public getType(name: string): Type {
    const info = this._namesMap.get(name);
    if (info === undefined) {
      throw new UnknownNameError(name);
    }
    return this._graph.getType(info.vertex);
  }

  public has(name: string): boolean {
    return this._namesMap.has(name);
  }

  public addOnChangedCallbackForT<T extends IValue>(
    name: string,
    func: OnChangedForCallbackT<T>
  ): CallbackSubscription {
    if (!this._namesMap.has(name)) {
      throw new UnknownNameError(name);
    }
    let mgr = this._onChangedCallbackManagerMap.get(name);
    if (!mgr) {
      mgr = new CallbackManager<OnChangedForCallbackT<IValue>>();
      this._onChangedCallbackManagerMap.set(name, mgr);
    }
    return mgr.subscribe((value: unknown): void => {
      func(value as T);
    });
  }

  public addOnChangedCallbackT<T extends IValue>(func: OnChangedCallbackT<T>): CallbackSubscription {
    return this._onChangedCallbackManager.subscribe((name: string, value: unknown): void => {
      func(name, value as T);
    });
  }

  public addOnChangedCallback(func: OnChangedCallbackT<IValue>): CallbackSubscription {
    return this._onChangedCallbackManager.subscribe(func);
  }

  public addOnFailedCallback(func: OnFailedCallback): CallbackSubscription {
    return this._onFailedCallbackManager.subscribe(func);
  }

  public getInputs(): Array<string> {
    const ret: Array<string> = [];
    this._namesMap.forEach((value: BuildInfo, key: string): void => {
      if (value.isInputVertex) {
        ret.push(key);
      }
    });
    return ret;
  }

  public getOutputs(): Array<string> {
    const ret: Array<string> = [];
    this._namesMap.forEach((value: BuildInfo, key: string): void => {
      if (!value.isInputVertex) {
        ret.push(key);
      }
    });
    return ret;
  }

  public getAll(): Array<string> {
    return Array.from(this._namesMap.keys());
  }

  public exportVertexInfo(io: ITypedValueJsonIO): Array<VertexInfo> {
    const ret: Array<VertexInfo> = [];
    const adj = this._graph.getAdjacencyList();
    this._graph.topologicalSort().forEach((d: VertexDependencies): void => {
      const calculator = this._graph.getValueCalculator(d.vertex);
      const inputs = adj.getInputs(d.vertex);
      const isInputVertex = inputs.length == 0;
      const isNamedCalculator = calculator.name !== undefined;
      if (isInputVertex == isNamedCalculator) {
        throw new Error('Input calculators must be unnamed. Non input calculators must be named.');
      }
      let valueJson: unknown = undefined;
      if (isInputVertex) {
        const typedValue = this._graph.getValue(d.vertex);
        if (typedValue !== undefined) {
          valueJson = io.export(typedValue.type, typedValue.value);
        }
      }
      ret.push({
        id: d.vertex,
        valueType: calculator.resProto.name,
        name: this._namedVertexMap.get(d.vertex)?.vertexName,
        value: valueJson,
        calculator: calculator.name
          ? {
              name: calculator.name,
              dependencies: inputs
            }
          : undefined
      });
    });
    return ret;
  }

  public async importVertexInfo(io: ITypedValueJsonIO, info: Array<VertexInfo>): Promise<void> {
    const buildInfoMap = new Map<number, BuildInfo>();
    const verticesToCalculate = new Array<VertexDescriptor>();
    info.forEach((value: VertexInfo): void => {
      const resType = TypesRegistry.get(value.valueType);
      if (!resType) {
        throw new Error('Unknown Type: [' + value.valueType + ']');
      }
      const inpOrder: Array<VertexDescriptor> = [];
      let calculator: IValueCalculator | undefined = undefined;
      let shouldCalculateVertex = false;
      if (value.calculator === undefined) {
        const val = io.import(value.value, value.valueType, value.name);
        const typedValue = val !== undefined ? TypedValue.FromValue(val) : undefined;
        calculator = new InputValueCalculator(resType, typedValue);
        shouldCalculateVertex = typedValue !== undefined;
      } else {
        const functor = FunctorsRegistry.get(value.calculator.name);
        if (!functor) {
          throw new Error('Unknown Functor: [' + value.calculator.name + ']');
        }
        const inpProto: Array<Type> = [];
        value.calculator.dependencies.forEach((vertex: number): void => {
          const buildInfo = buildInfoMap.get(vertex);
          if (!buildInfo) {
            throw new Error('Unknown Vertex');
          }
          inpProto.push(buildInfo.type);
          inpOrder.push(buildInfo.vertex);
        });
        calculator = new FunctorValueCalculator(functor, inpProto, resType);
      }
      const vertex = this._graph.initVertex(calculator, inpOrder, value.id);
      const buildInfo = new BuildInfo(this._namesMap, resType, vertex, value.calculator === undefined);
      buildInfoMap.set(value.id, buildInfo);
      if (value.name !== undefined) {
        buildInfo.vertexName = value.name;
        this._namesMap.set(value.name, buildInfo);
        this._namedVertexMap.set(vertex, buildInfo);
      }
      if (shouldCalculateVertex) {
        verticesToCalculate.push(vertex);
      }
    });
    const dependencies = this._graph.topologicalSort(verticesToCalculate);
    await this._graph.calculateVerticesAsync(dependencies);
  }

  public exportExtensionInfo(): Array<ExtensionInfo> {
    const ret: Array<ExtensionInfo> = [];
    for (const [key, val] of this._algorithmExtensions) {
      ret.push({
        id: key,
        vertices: val
      });
    }
    return ret;
  }

  public importExtensionInfo(info: Array<ExtensionInfo>): void {
    for (const entry of info) {
      this._algorithmExtensions.set(entry.id, entry.vertices);
    }
  }

  // For Unit Testing
  public __setGraph(graph: Graph): void {
    this._graph = graph;
  }
}
